import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IMaskModule } from 'angular-imask';
import { Observable, tap } from 'rxjs';
import { nameOf } from '../../../../../../../../core/functions/string-helpers';
import { DropdownOption } from '../../../../../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../../../../../core/models/form-group-type';
import { RegExPattern } from '../../../../../../../../core/models/regexp-pattern/regex-pattern';
import { DropdownService } from '../../../../../../../../core/services/resources/dropdown.service';
import { DraggableModalWrapperComponent } from '../../../../../../../../shared/components/modal/draggable-modal-wrapper/draggable-modal-wrapper.component';
import { RegexInputDirective } from '../../../../../../../../shared/directives/regex-input/regex-input.directive';
import { PaymentValidationMessages } from '../../../../../shared/constants/payment-validation-messages';
import { PaymentService } from '../../../../../shared/services/payment/payment.service';
import { PaymentTransactionMaskConfig } from '../../../../constants/payment-transaction-mask-config';
import { ConfirmPaymentRequest } from '../../../../models/confirm-payment-request';
import { ConfirmPaymentFormDialogModalData } from './models/confirm-payment-form-dialog-modal-data';
import { ConfirmPaymentFormModel } from './models/confirm-payment-form-model';
import { ConfirmPaymentFormService } from './services/confirm-payment-form.service';

@Component({
    selector: 'app-confirm-payment-form-dialog',
    templateUrl: './confirm-payment-form-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatCardModule,
        MatButtonModule,
        MatDialogContent,
        MatSelectModule,
        MatDialogActions,
        MatDialogTitle,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        IMaskModule,
        DraggableModalWrapperComponent,
        RegexInputDirective,
        MatTooltipModule,
    ],
})
export class ConfirmPaymentFormDialogComponent {
    form: FormGroup<FormGroupType<ConfirmPaymentFormModel>> =
        this.confirmPaymentFormService.getFormDefinition(this.data.balance);

    paymentMethodDropdownOptions: DropdownOption[] =
        this.dropdownService.getInvoiceNet30TransitionablePaymentMethods();

    readonly paymentTransactionAmountMaskConfig =
        PaymentTransactionMaskConfig.amount;
    readonly regexPattern = RegExPattern;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public readonly data: ConfirmPaymentFormDialogModalData,
        private readonly paymentService: PaymentService,
        private readonly dropdownService: DropdownService,
        private readonly confirmPaymentFormService: ConfirmPaymentFormService,
        private readonly dialogRef: MatDialogRef<ConfirmPaymentFormDialogComponent>,
    ) {}

    getAmountMaxError(): string | undefined {
        const errors = this.form.controls.amount.errors;

        if (!errors) {
            return undefined;
        }

        return errors[
            nameOf<typeof PaymentValidationMessages>('amountExceedsBalance')
        ] as string | undefined;
    }

    save(): void {
        if (this.form.valid) {
            this.saveFormDataToApi(
                this.data.orderId,
                this.data.paymentId,
                this.generateRequestObject(),
            )
                .pipe(
                    // After successfully saving the form data to the API, we can close the dialog
                    // and pass a result indicating success
                    tap(() => this.dialogRef.close(true)),
                )
                .subscribe();
        }
    }

    cancel(): void {
        this.dialogRef.close(false);
    }

    private saveFormDataToApi(
        orderId: string,
        paymentId: string,
        request: ConfirmPaymentRequest,
    ): Observable<void> {
        return this.paymentService.confirm(orderId, paymentId, request);
    }

    private generateRequestObject(): ConfirmPaymentRequest {
        return {
            ...this.form.value,
        } as ConfirmPaymentRequest;
    }
}
